import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, ShoppingBag, Search, Filter, UtensilsCrossed } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import ProductDetailModal from '../components/ProductDetailModal';
import ChefPickBadge from '../components/ChefPickBadge';
import { localizedField as i18nLocalized } from '../utils/i18nFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Alakart = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const { addItem } = useCart();
  const { language } = useLanguage();

  // Category translations - match DB category keys
  const categoryTranslations = {
    tr: { 
      all: 'Tümü', 
      'Kahvaltı': 'Kahvaltı',
      'Tostlar': 'Tostlar',
      'Menemenler': 'Menemenler',
      'Omletler': 'Omletler',
      'Burgerler': 'Burgerler',
      'Soğuk Sandwichler': 'Soğuk Sandviçler',
      'Sıcak Sandwichler': 'Sıcak Sandviçler',
      'Ana Yemekler': 'Ana Yemekler',
      'Makarnalar': 'Makarnalar',
      'Salatalar': 'Salatalar',
      'Mezeler': 'Mezeler',
      'Ekstralar': 'Ekstralar',
    },
    en: { 
      all: 'All', 
      'Kahvaltı': 'Breakfast',
      'Tostlar': 'Toasts',
      'Menemenler': 'Menemen',
      'Omletler': 'Omelettes',
      'Burgerler': 'Burgers',
      'Soğuk Sandwichler': 'Cold Sandwiches',
      'Sıcak Sandwichler': 'Hot Sandwiches',
      'Ana Yemekler': 'Main Dishes',
      'Makarnalar': 'Pasta',
      'Salatalar': 'Salads',
      'Mezeler': 'Appetizers',
      'Ekstralar': 'Extras',
    },
    me: { 
      all: 'Sve', 
      'Kahvaltı': 'Doručak',
      'Tostlar': 'Tost Sendviči',
      'Menemenler': 'Menemeni',
      'Omletler': 'Omleti',
      'Burgerler': 'Burgeri',
      'Soğuk Sandwichler': 'Hladni Sendviči',
      'Sıcak Sandwichler': 'Topli Sendviči',
      'Ana Yemekler': 'Glavna Jela',
      'Makarnalar': 'Tjestenina',
      'Salatalar': 'Salate',
      'Mezeler': 'Predjela',
      'Ekstralar': 'Dodaci',
    },
    ru: { 
      all: 'Все', 
      'Kahvaltı': 'Завтрак',
      'Tostlar': 'Тосты',
      'Menemenler': 'Менемен',
      'Omletler': 'Омлеты',
      'Burgerler': 'Бургеры',
      'Soğuk Sandwichler': 'Холодные сэндвичи',
      'Sıcak Sandwichler': 'Горячие сэндвичи',
      'Ana Yemekler': 'Основные блюда',
      'Makarnalar': 'Паста',
      'Salatalar': 'Салаты',
      'Mezeler': 'Закуски',
      'Ekstralar': 'Дополнения',
    },
    sr: { 
      all: 'Све', 
      'Kahvaltı': 'Доручак',
      'Tostlar': 'Тостови',
      'Menemenler': 'Менемени',
      'Omletler': 'Омлети',
      'Burgerler': 'Бургери',
      'Soğuk Sandwichler': 'Хладни сендвичи',
      'Sıcak Sandwichler': 'Топли сендвичи',
      'Ana Yemekler': 'Главна јела',
      'Makarnalar': 'Тестенина',
      'Salatalar': 'Салате',
      'Mezeler': 'Предјела',
      'Ekstralar': 'Додаци',
    }
  };

  const texts = {
    tr: { title: 'Alakart Menü', subtitle: 'Zengin menümüzden seçiminizi yapın', searchPlaceholder: 'Yemek ara...', addToCart: 'Sepete Ekle', added: 'Eklendi', loading: 'Yükleniyor...', noResults: 'Aradığınız kriterlere uygun yemek bulunamadı.' },
    en: { title: 'A La Carte Menu', subtitle: 'Choose from our rich menu', searchPlaceholder: 'Search dishes...', addToCart: 'Add to Cart', added: 'Added', loading: 'Loading...', noResults: 'No dishes found matching your criteria.' },
    me: { title: 'A La Carte Meni', subtitle: 'Izaberite iz našeg bogatog menija', searchPlaceholder: 'Pretraži jela...', addToCart: 'Dodaj u Korpu', added: 'Dodato', loading: 'Učitavanje...', noResults: 'Nema jela koja odgovaraju vašim kriterijumima.' },
    ru: { title: 'Меню À La Carte', subtitle: 'Выберите из нашего богатого меню', searchPlaceholder: 'Поиск блюд...', addToCart: 'В корзину', added: 'Добавлено', loading: 'Загрузка...', noResults: 'Блюда по вашим критериям не найдены.' },
    sr: { title: 'А Ла Карт Мени', subtitle: 'Изаберите из нашег богатог менија', searchPlaceholder: 'Претражи јела...', addToCart: 'Додај у корпу', added: 'Додато', loading: 'Учитавање...', noResults: 'Нема јела по вашим критеријумима.' }
  };

  const txt = texts[language] || texts.en;
  const catTrans = categoryTranslations[language] || categoryTranslations.en;

  // Get item name/description based on language (covers tr/en/me/ru/sr with fallback)
  const getItemName = (item) => i18nLocalized(item, 'name', language);
  const getItemDescription = (item) => i18nLocalized(item, 'description', language);

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    try {
      const [menuRes, catRes] = await Promise.all([
        axios.get(`${API}/menu`),
        axios.get(`${API}/menu/categories`)
      ]);
      setMenuItems(menuRes.data);
      setCategories(catRes.data);
    } catch (error) {
      console.error('Menu fetch error:', error);
      toast.error('Menü yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const itemName = getItemName(item).toLowerCase();
    const itemDesc = getItemDescription(item).toLowerCase();
    const matchesSearch = itemName.includes(searchQuery.toLowerCase()) ||
                         itemDesc.includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleAddToCart = (item) => {
    if (item.is_available === false) return;
    addItem({
      item_id: item.id,
      name: getItemName(item),
      name_tr: item.name,
      price: item.price,
      quantity: 1,
      item_type: 'alakart'
    });
    toast.success(`${getItemName(item)} ${txt.added}`);
  };

  const handleModalAdd = (item, qty) => {
    addItem({
      item_id: item.id,
      name: getItemName(item),
      name_tr: item.name,
      price: item.price,
      quantity: qty,
      item_type: 'alakart'
    });
    toast.success(`${qty}x ${getItemName(item)} ${txt.added}`);
  };

  const openDetail = (item) => {
    setSelectedItem({
      ...item,
      _displayName: getItemName(item),
      _displayDesc: getItemDescription(item),
      _displayCategory: catTrans[item.category] || item.category
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-terracotta border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      {/* Header */}
      <div className="bg-sand py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal mb-4">
              {txt.title}
            </h1>
            <p className="text-charcoal/60 text-lg">
              {txt.subtitle}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-charcoal/40" />
            <Input
              type="text"
              placeholder={txt.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-white border-stone-200 rounded-xl h-12"
              data-testid="menu-search-input"
            />
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('all')}
              className={`rounded-full whitespace-nowrap ${
                selectedCategory === 'all' 
                  ? 'bg-basil text-white hover:bg-basil/90' 
                  : 'bg-white hover:bg-stone-50'
              }`}
              data-testid="category-all-btn"
            >
              {catTrans.all || 'All'}
            </Button>
            {categories.map(cat => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(cat)}
                className={`rounded-full whitespace-nowrap ${
                  selectedCategory === cat 
                    ? 'bg-basil text-white hover:bg-basil/90' 
                    : 'bg-white hover:bg-stone-50'
                }`}
                data-testid={`category-${cat.toLowerCase().replace(' ', '-')}-btn`}
              >
                {catTrans[cat] || cat}
              </Button>
            ))}
          </div>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
                layout
              >
                <MenuCard item={item} onAddToCart={handleAddToCart} onDetail={openDetail} catTrans={catTrans} getItemName={getItemName} getItemDescription={getItemDescription} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-16">
            <p className="text-charcoal/60 text-lg">
              {txt.noResults}
            </p>
          </div>
        )}
      </div>

      <ProductDetailModal
        item={selectedItem}
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        onAddToCart={handleModalAdd}
        language={language}
      />
    </div>
  );
};

const MenuCard = ({ item, onAddToCart, onDetail, catTrans, getItemName: getItemNameProp, getItemDescription: getItemDescProp }) => {
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();
  const { language } = useLanguage();

  const getItemName = getItemNameProp || ((i) => i18nLocalized(i, 'name', language));
  const getItemDescription = getItemDescProp || ((i) => i18nLocalized(i, 'description', language));

  const texts = {
    tr: { addBtn: 'Ekle', outOfStock: 'Satista Degil', addedMsg: 'sepete eklendi' },
    en: { addBtn: 'Add', outOfStock: 'Not Available', addedMsg: 'added to cart' },
    me: { addBtn: 'Dodaj', outOfStock: 'Nije Dostupno', addedMsg: 'dodato u korpu' },
    ru: { addBtn: 'Добавить', outOfStock: 'Нет в наличии', addedMsg: 'добавлено в корзину' },
    sr: { addBtn: 'Додај', outOfStock: 'Није доступно', addedMsg: 'додато у корпу' }
  };

  const txt = texts[language] || texts.en;
  const itemName = getItemName(item);

  const handleAdd = () => {
    addItem({
      item_id: item.id,
      name: itemName,
      name_tr: item.name,
      price: item.price,
      quantity: quantity,
      item_type: 'alakart'
    });
    toast.success(`${quantity}x ${itemName} ${txt.addedMsg}`);
    setQuantity(1);
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-stone-100/50 menu-card cursor-pointer" onClick={() => onDetail && onDetail(item)} data-testid={`menu-card-${item.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
        {item.image_url ? (
          <img
            src={item.image_url.startsWith('/api') ? `${API.replace('/api', '')}${item.image_url}` : item.image_url}
            alt={itemName}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : null}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <UtensilsCrossed className="w-12 h-12 text-stone-300" />
        </div>
        {!item.is_available && (
          <div className="absolute inset-0 bg-charcoal/60 flex items-center justify-center">
            <span className="text-white font-medium">{txt.outOfStock}</span>
          </div>
        )}
        {item.is_featured && (
          <div className="absolute top-3 left-3"><ChefPickBadge language={language} /></div>
        )}
      </div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-playfair text-xl font-semibold text-charcoal">
            {itemName}
          </h3>
          <span className="text-terracotta font-bold text-lg">
            €{item.price}
          </span>
        </div>
        <p className="text-charcoal/60 text-sm mb-4 line-clamp-2">
          {getItemDescription(item)}
        </p>
        <span className="inline-block px-3 py-1 bg-sand rounded-full text-xs font-medium text-charcoal/70 mb-4">
          {catTrans[item.category] || item.category}
        </span>
        
        {item.is_available && (
          <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 bg-stone-100 rounded-full p-1">
              <button
                onClick={() => setQuantity(Math.max(0.5, quantity - 0.5))}
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-stone-50 transition-colors"
                data-testid={`quantity-minus-${item.id}`}
              >
                <Minus className="w-4 h-4 text-charcoal" />
              </button>
              <span className="w-8 text-center font-medium text-charcoal">
                {quantity % 1 === 0 ? quantity : quantity.toFixed(1)}
              </span>
              <button
                onClick={() => setQuantity(quantity + 0.5)}
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-stone-50 transition-colors"
                data-testid={`quantity-plus-${item.id}`}
              >
                <Plus className="w-4 h-4 text-charcoal" />
              </button>
            </div>
            <Button
              onClick={handleAdd}
              className="flex-1 bg-terracotta text-white hover:bg-terracotta/90 rounded-full"
              data-testid={`add-to-cart-${item.id}`}
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              {txt.addBtn}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Alakart;
